import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { adminLive, adminMe } from "@/lib/adminApi";
import useCountdown, { isRolling } from "@/lib/useCountdown";
import { useNavigate } from "react-router-dom";

export default function AdminLiveScreen() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    document.title = "LIVE Broadcast · Wolfkeeper";
    adminMe().catch(() => nav("/admin/login"));
    const load = () => adminLive(slug).then(setData).catch((e) => setErr(e?.response?.data?.detail || "error"));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [slug, nav]);

  if (err) return <div className="min-h-screen bg-black text-white grid place-items-center">{err}</div>;
  if (!data) return <div className="min-h-screen bg-black text-white grid place-items-center">Loading auction…</div>;

  return <LiveScreenBody data={data} />;
}

function LiveScreenBody({ data }) {
  const { package: pkg, highest_qualified_bid, remaining, total, approved_bidders, sold_certificates, revenue, next_package } = data;
  const cd = useCountdown(pkg.window_end);
  const soldOut = pkg.has_capacity && remaining <= 0;

  return (
    <div className="min-h-screen bg-black text-white p-6 md:p-10" data-testid="admin-live-screen">
      {/* Top row */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#c8102e] text-white px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> Live
          </span>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876]">Global Wolfkeeper Auction · Broadcast Screen</div>
        </div>
        <div className="stat-num text-lg text-[#e8c876]">{new Date().toLocaleTimeString()}</div>
      </div>

      {/* Hero: Active */}
      <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#0a0908] via-[#1a1917] to-[#0a0908] border border-[#c9a24b]/30 p-6 md:p-10 mb-6">
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876]">Active auction · {pkg.city}</div>
            <h1 className="mt-2 font-display text-[clamp(2rem,6vw,5rem)] leading-[0.95] tracking-[-0.02em]">
              {pkg.title}
              {pkg.subtitle && <span className="block italic text-lg md:text-2xl mt-2 text-white/70">{pkg.subtitle}</span>}
            </h1>
            <div className="mt-4 text-white/70">{pkg.window_label}</div>
            {soldOut && <div className="mt-4 inline-block text-2xl font-bold tracking-[0.2em] uppercase text-[#c8102e] border-2 border-[#c8102e] px-4 py-2 rounded-lg">SOLD OUT</div>}
          </div>

          {/* Big stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">Highest qualified bid</div>
              <div className="stat-num text-4xl md:text-5xl text-[#e8c876] mt-2" data-testid="live-highest-bid">${(highest_qualified_bid || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">Remaining inventory</div>
              <div className="stat-num text-4xl md:text-5xl text-white mt-2" data-testid="live-remaining">
                {pkg.has_capacity ? `${remaining}/${total}` : "Open"}
              </div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">Approved bidders</div>
              <div className="stat-num text-4xl md:text-5xl text-white mt-2" data-testid="live-approved-count">{approved_bidders.length}</div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">Revenue this package</div>
              <div className="stat-num text-4xl md:text-5xl text-[#e8c876] mt-2" data-testid="live-revenue">${(revenue || 0).toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Countdown */}
        <div className="mt-6" data-testid="live-countdown">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876] mb-2">Countdown to window close</div>
          {isRolling(pkg.window_end) ? (
            <div className="stat-num text-2xl text-white">Always On</div>
          ) : (
            <div className="grid grid-cols-4 gap-3 max-w-xl">
              {[["Days", cd.d], ["Hours", cd.h], ["Min", cd.m], ["Sec", cd.s]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-white/5 border border-white/10 py-4 text-center">
                  <div className="stat-num text-3xl md:text-4xl text-white">{String(v).padStart(2, "0")}</div>
                  <div className="text-[9px] tracking-[0.24em] uppercase text-white/60 mt-1">{l}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approved bidders + certificates */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="live-approved-list">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876] mb-4">Approved bidders</div>
          {approved_bidders.length === 0 ? <div className="text-white/50 text-sm">No approved bidders yet.</div> : (
            <ul className="divide-y divide-white/10">
              {approved_bidders.map((b) => (
                <li key={b.app} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-white/95">{b.name} · <span className="text-white/50 text-sm">{b.city}</span></div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">{b.tiktok || b.app}</div>
                  </div>
                  <div className="text-right">
                    <div className="stat-num text-lg text-[#e8c876]">${b.bid.toLocaleString()}</div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">{b.status}{b.cert ? ` · ${b.cert}` : ""}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="live-sold-certs">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876] mb-4">Sold certificate numbers</div>
          {sold_certificates.length === 0 ? <div className="text-white/50 text-sm">No certificates sold yet for this package.</div> : (
            <div className="flex flex-wrap gap-2">
              {sold_certificates.map((c) => (
                <span key={c} className="font-mono text-[13px] tracking-wider px-3 py-1.5 rounded-full bg-[#e8c876] text-black">{c}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Next package */}
      {next_package && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 flex items-center justify-between flex-wrap gap-3" data-testid="live-next-pkg">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876]">Next package on the block</div>
            <div className="font-display text-2xl md:text-3xl mt-1">{next_package.title} · <span className="text-white/60">{next_package.city}</span></div>
            <div className="text-white/60 text-sm">{next_package.window_label} · {next_package.price_label || `$${next_package.opening_bid.toLocaleString()}`}</div>
          </div>
          <div className="stat-num text-2xl text-[#e8c876]">{next_package.remaining_slots}/{next_package.total_slots}</div>
        </div>
      )}
    </div>
  );
}
